import { createContext, useContext, useState, useEffect, useMemo, useRef, type ReactNode, type Dispatch, type SetStateAction } from "react";
import type { TestConfig, Blueprint, Question } from "@shared/schema";
import { saveTestState, clearTestState, clearSavedTestId, loadTestState, type ChatMessage } from "@/utils/localStorage";
import { useToast } from "@/hooks/use-toast";
import { resetChatHalfHeight } from "@/hooks/use-chat-half-height";
import { resetChatNarrowWidth } from "@/hooks/use-chat-narrow-width";
import { resetChatExtended } from "@/hooks/use-chat-extended";

interface TestContextType {
  config: TestConfig | null;
  setConfig: (config: TestConfig) => void;
  blueprint: Blueprint | null;
  // Accepts null to support "regenerate" flows that clear the blueprint
  // so the build-blueprint page kicks off a fresh fetch. Also accepts a
  // functional updater so concurrent amendments apply against the latest
  // committed blueprint rather than a stale captured snapshot.
  setBlueprint: Dispatch<SetStateAction<Blueprint | null>>;
  questions: Question[];
  setQuestions: Dispatch<SetStateAction<Question[]>>;
  directions: string;
  setDirections: (directions: string) => void;
  testName: string;
  setTestName: (name: string) => void;
  // Single shared conversation that all AI Assistant panes read/write.
  chatMessages: ChatMessage[];
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  resetTest: () => void;
  loadSavedState: (state: { config: TestConfig | null; blueprint: Blueprint | null; questions: Question[]; directions?: string; chatMessages?: ChatMessage[] }) => void;
}

const TestContext = createContext<TestContextType | undefined>(undefined);

export function TestProvider({ children }: { children: ReactNode }) {
  // Restore previously saved state once on mount. This is what makes a page
  // refresh land the user back on the same step with their data intact —
  // without it, BuildBlueprint would regenerate the blueprint on every
  // refresh because context starts empty.
  const initialState = useMemo(() => loadTestState(), []);

  const [config, setConfig] = useState<TestConfig | null>(initialState?.config ?? null);
  const [blueprint, setBlueprint] = useState<Blueprint | null>(initialState?.blueprint ?? null);
  const [questions, setQuestions] = useState<Question[]>(initialState?.questions ?? []);
  const [directions, setDirections] = useState<string>(initialState?.directions ?? "");
  const [testName, setTestName] = useState(initialState?.testName ?? "Untitled Test");
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>(initialState?.chatMessages ?? []);

  const { toast } = useToast();
  // Latch so we only toast the quota error once per session — would
  // otherwise fire on every debounced save.
  const quotaWarnedRef = useRef(false);
  // Holds the pending debounced-save timer so resetTest can cancel it
  // synchronously. Without this, a save scheduled before a reset (its callback
  // closing over the OLD state) can fire just after resetTest clears storage
  // and resurrect the old draft.
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mirror the latest snapshot into a ref so the beforeunload / pagehide
  // flush below can read the freshest values without depending on whatever
  // closure the listener was registered with.
  const latestStateRef = useRef({ config, blueprint, questions, directions, chatMessages, testName });
  latestStateRef.current = { config, blueprint, questions, directions, chatMessages, testName };

  // Auto-save to localStorage whenever state changes (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Only save if there's meaningful state (config, blueprint, questions, or chat)
      if (config || blueprint || questions.length > 0 || chatMessages.length > 0) {
        const currentStep = getCurrentStep();
        const result = saveTestState({
          config,
          blueprint,
          questions,
          directions,
          chatMessages,
          currentStep,
          testName,
        });
        if (!result.ok && result.reason === 'quota' && !quotaWarnedRef.current) {
          quotaWarnedRef.current = true;
          toast({
            title: 'Storage limit reached',
            description:
              'Your test got too large to auto-save in this browser. Recent changes may not survive a refresh. Remove a material or finish the flow to shrink it.',
            variant: 'destructive',
          });
        }
      } else {
        // No meaningful state (e.g. right after a reset) — make sure storage is
        // empty rather than leaving a stale draft behind.
        clearTestState();
      }
    }, 1000); // Debounce saves by 1 second

    saveTimerRef.current = timeoutId;
    return () => clearTimeout(timeoutId);
  }, [config, blueprint, questions, directions, chatMessages, testName, toast]);

  // If the page is about to unload (refresh, navigation, tab close) while a
  // debounced save is still pending, an edit made in the last second would
  // be lost. Flush synchronously via beforeunload + pagehide so the next
  // load picks up the latest state. pagehide covers iOS Safari + bfcache
  // where beforeunload doesn't reliably fire.
  useEffect(() => {
    const flush = () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      const s = latestStateRef.current;
      if (s.config || s.blueprint || s.questions.length > 0 || s.chatMessages.length > 0) {
        const currentStep =
          s.questions.length > 0 ? 5 : s.blueprint ? 3 : s.config ? 2 : 1;
        saveTestState({
          config: s.config,
          blueprint: s.blueprint,
          questions: s.questions,
          directions: s.directions,
          chatMessages: s.chatMessages,
          currentStep,
          testName: s.testName,
        });
      }
    };
    window.addEventListener("beforeunload", flush);
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("beforeunload", flush);
      window.removeEventListener("pagehide", flush);
    };
  }, []);

  const getCurrentStep = (): number => {
    if (questions.length > 0) return 5;
    if (blueprint) return 3;
    if (config) return 2;
    return 1;
  };

  const loadSavedState = (state: { config: TestConfig | null; blueprint: Blueprint | null; questions: Question[]; directions?: string; chatMessages?: ChatMessage[] }) => {
    setConfig(state.config);
    setBlueprint(state.blueprint);
    setQuestions(state.questions);
    setDirections(state.directions || "");
    setChatMessages(state.chatMessages || []);
  };

  const resetTest = () => {
    // Cancel any in-flight debounced save FIRST — otherwise its callback (which
    // captured the pre-reset state) can fire after clearTestState() and write
    // the old draft back, resurrecting it on the next load.
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    setConfig(null);
    setBlueprint(null);
    setQuestions([]);
    setDirections("");
    setTestName("Untitled Test");
    setChatMessages([]);
    clearTestState(); // Clear localStorage when resetting
    clearSavedTestId(); // Forget the saved DB id so the next test saves fresh
    // Restore the assistant size preferences to their defaults (narrow +
    // short) so a new test always starts in the slim layout, regardless
    // of what the user chose during the previous test.
    resetChatHalfHeight();
    resetChatNarrowWidth();
    resetChatExtended();
  };

  return (
    <TestContext.Provider
      value={{
        config,
        setConfig,
        blueprint,
        setBlueprint,
        questions,
        setQuestions,
        directions,
        setDirections,
        testName,
        setTestName,
        chatMessages,
        setChatMessages,
        resetTest,
        loadSavedState,
      }}
    >
      {children}
    </TestContext.Provider>
  );
}

export function useTest() {
  const context = useContext(TestContext);
  if (!context) {
    throw new Error("useTest must be used within a TestProvider");
  }
  return context;
}
